/**
 * «История» — сырая лента строк отчётов смены (task 17 §3.10, table view §5.2;
 * перенесена с «Аудита» на «Учёт времени» в task 82 §1.4).
 *
 * Источник — `GET /api/admin/timesheet-audit` (см. §2.5). Доступ — `admin`
 * (видит все подразделения) и лид (scope-aware: только его прямая
 * бригада — фильтр на backend). Прочие роли → forbidden-notice (второй слой
 * защиты; первый — role-gate маршрута `/time-approval`).
 *
 * Admin-only фильтры («Только введённые админом», «Показать удалённые»,
 * «Только требующие внимания») рисуются только админу; лиду — лента своей
 * бригады без них (backend всё равно игнорирует admin-only флаги для лида).
 *
 * Семантика «рабочий перезаписал» = две отдельные строки таблицы:
 *   - старая с `deleted_reason='replaced'`,
 *   - новая без `deleted_reason`, тот же `(user_id, work_date)`.
 * Связь старая→новая собираем на фронте через `replaces_id` (followup).
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { addDays, format, parseISO } from "date-fns";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import {
  AlertTriangle,
  ExternalLink,
  HelpCircle,
  History,
  RefreshCw,
  Trash2,
  UserCog,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ApiErrorNotice } from "@/components/ui/api-error-notice";
import { DateField } from "@/components/ui/date-field";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  SortableTableHead,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useTimesheetAudit, useTimesheetAuditWorkers } from "@/hooks/useTimesheetAudit";
import { useProjects } from "@/hooks/useProjects";
import { useAdminWorkTypes } from "@/hooks/useAdminWorkTypes";
import { useMe } from "@/hooks/useMe";
import { actsOnUnit } from "@/store/session";
import type { TimesheetAuditEntry } from "@/api/adminTimesheetAudit";
import { formatDateTime, formatDate } from "@/lib/datetime";
import { formatHoursHalf, formatHmsCompact } from "@/lib/timesheet";
import { cn } from "@/lib/utils";

/** ISO-понедельник недели даты `YYYY-MM-DD` — чтобы карточка дня открылась
 * сразу на нужной неделе при переходе из «Истории» в «Подтверждение». */
function isoMonday(workDate: string): string {
  const d = parseISO(workDate);
  const dow = (d.getDay() + 6) % 7; // 0 = Monday
  return format(addDays(d, -dow), "yyyy-MM-dd");
}

interface UiFilters {
  workerId: number | "all";
  projectId: number | "all";
  workTypeId: number | "all";
  dateFrom: string;
  dateTo: string;
  showDeleted: boolean;
  needsAttention: boolean;
  adminBackfillOnly: boolean;
}

const EMPTY: UiFilters = {
  workerId: "all",
  projectId: "all",
  workTypeId: "all",
  dateFrom: "",
  dateTo: "",
  showDeleted: true,
  needsAttention: false,
  adminBackfillOnly: false,
};

interface AuditRow extends TimesheetAuditEntry {
  followupId: number | undefined;
}

export function TimesheetAuditTab(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const { user: me } = useMe();
  const isAdmin = me?.role === "admin";
  // task 82 §1.4: тот, кто действует на юнит (лид ИЛИ активный зам), видит
  // «Историю» замещаемого поддерева (scope на backend); прочие роли сюда не
  // попадают через маршрут, но держим forbidden-notice вторым слоём. Полномочие
  // выводится из орг-дерева и рёбер делегации (`acting_unit_ids`), не из роли
  // (task 97 → 98 §2.2).
  const canView = isAdmin || actsOnUnit(me);

  const [ui, setUi] = useState<UiFilters>(EMPTY);
  const [sorting, setSorting] = useState<SortingState>([{ id: "when", desc: true }]);

  const apiFilters = useMemo(
    () => ({
      user_id: ui.workerId === "all" ? undefined : ui.workerId,
      project_id: ui.projectId === "all" ? undefined : ui.projectId,
      work_type_id: ui.workTypeId === "all" ? undefined : ui.workTypeId,
      date_from: ui.dateFrom || undefined,
      date_to: ui.dateTo || undefined,
      // Admin-only фильтры — только админу; лиду не отправляем (backend всё
      // равно их игнорирует, но не плодим бессмысленные query-параметры).
      include_deleted: isAdmin ? ui.showDeleted : true,
      needs_attention: isAdmin ? ui.needsAttention || undefined : undefined,
      admin_backfill_only: isAdmin ? ui.adminBackfillOnly || undefined : undefined,
    }),
    [ui, isAdmin],
  );

  const feed = useTimesheetAudit(apiFilters, canView);
  const workers = useTimesheetAuditWorkers(canView);
  const projectsQ = useProjects({ limit: 200 });
  // `/admin/work-types` — admin-only ручка; лиду фильтр по виду работ не
  // показываем (его лента и так сужена до бригады), запрос не шлём.
  const workTypesQ = useAdminWorkTypes(isAdmin);

  const allEntries = useMemo<TimesheetAuditEntry[]>(
    () => (feed.data?.pages ?? []).flat(),
    [feed.data],
  );

  const workerById = useMemo(() => {
    const m = new Map<number, { name: string; tab_number: string | null }>();
    (workers.data ?? []).forEach((w) =>
      m.set(w.id, { name: w.display_name, tab_number: w.tab_number ?? null }),
    );
    return m;
  }, [workers.data]);

  const projectById = useMemo(() => {
    const m = new Map<number, string>();
    (projectsQ.data ?? []).forEach((p) => {
      if (typeof p.id === "number") m.set(p.id, p.name);
    });
    return m;
  }, [projectsQ.data]);

  const workTypeById = useMemo(() => {
    const m = new Map<number, string>();
    (workTypesQ.data ?? []).forEach((w) => {
      if (typeof w.id === "number") m.set(w.id, w.name);
    });
    return m;
  }, [workTypesQ.data]);

  // Followup map (replaces_id of newer entry → followup entry id) — для
  // tooltip «заменено записью №N» на старой строке.
  const followupByReplaces = useMemo(() => {
    const m = new Map<number, number>();
    for (const e of allEntries) {
      if (typeof e.replaces_id === "number") m.set(e.replaces_id, e.id);
    }
    return m;
  }, [allEntries]);

  const rows = useMemo<AuditRow[]>(
    () => allEntries.map((e) => ({ ...e, followupId: followupByReplaces.get(e.id) })),
    [allEntries, followupByReplaces],
  );

  const columns = useMemo<ColumnDef<AuditRow>[]>(
    () => [
      {
        id: "entryId",
        accessorKey: "id",
        header: t("audit.timesheet.columns.entryId"),
        cell: ({ row }) => (
          <span className="text-muted-foreground text-xs tabular-nums">№{row.original.id}</span>
        ),
      },
      {
        id: "when",
        accessorKey: "created_at",
        header: t("audit.timesheet.columns.when"),
        cell: ({ row }) => (
          <time
            dateTime={row.original.created_at}
            className="text-muted-foreground text-xs tabular-nums"
          >
            {formatDateTime(row.original.created_at, locale)}
          </time>
        ),
      },
      {
        id: "worker",
        accessorFn: (r) => r.user.display_name,
        header: t("audit.timesheet.columns.worker"),
        cell: ({ row }) => (
          <div className="flex min-w-0 flex-col">
            <span className="truncate font-medium tracking-tight">
              {row.original.user.display_name}
            </span>
            {row.original.user.tab_number && (
              <span className="text-muted-foreground text-xs tabular-nums">
                {t("audit.timesheet.tab", { value: row.original.user.tab_number })}
              </span>
            )}
          </div>
        ),
      },
      {
        id: "workDate",
        accessorKey: "work_date",
        header: t("audit.timesheet.columns.workDate"),
        cell: ({ row }) => (
          <span className="text-sm tabular-nums">{formatDate(row.original.work_date, locale)}</span>
        ),
      },
      {
        id: "project",
        accessorFn: (r) => r.project?.name ?? r.project_code_raw ?? "",
        enableSorting: false,
        header: t("audit.timesheet.columns.project"),
        cell: ({ row }) => {
          const e = row.original;
          const isUnknown = e.project == null && e.project_code_raw != null;
          // §20/2.1 — оба NULL = осознанный «без проекта».
          const isSkipped = e.project == null && e.project_code_raw == null;
          const struck = e.deleted_reason === "replaced" || e.deleted_reason === "user_delete";
          if (isSkipped) {
            return (
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 text-sm text-muted-foreground italic",
                  struck && "line-through decoration-1",
                )}
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <AlertTriangle
                      data-testid={`audit-skipped-warn-${e.id}`}
                      className="h-3.5 w-3.5 shrink-0 text-[var(--tone-caution-fg)]"
                      aria-label={t("audit.timesheet.skippedProject")}
                    />
                  </TooltipTrigger>
                  <TooltipContent>{t("audit.timesheet.skippedProject")}</TooltipContent>
                </Tooltip>
                <span className="truncate">{t("audit.timesheet.noProject")}</span>
              </span>
            );
          }
          if (isUnknown) {
            return (
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 text-sm text-[var(--tone-caution-fg)]",
                  struck && "text-muted-foreground line-through decoration-1",
                )}
                data-testid={`audit-project-${e.id}`}
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <AlertTriangle
                      className="h-3.5 w-3.5 shrink-0"
                      aria-label={t("audit.timesheet.unknownProject")}
                    />
                  </TooltipTrigger>
                  <TooltipContent>{t("audit.timesheet.unknownProject")}</TooltipContent>
                </Tooltip>
                <span className="truncate">{e.project_code_raw ?? "—"}</span>
              </span>
            );
          }
          if (e.project == null) {
            return <span className="text-muted-foreground text-sm">—</span>;
          }
          const showCode = e.project.code !== e.project.name;
          return (
            <div className="flex min-w-0 flex-col" data-testid={`audit-project-${e.id}`}>
              <span
                data-testid={`audit-project-name-${e.id}`}
                className={cn(
                  "truncate font-medium tracking-tight",
                  struck && "text-muted-foreground line-through decoration-1",
                )}
              >
                {e.project.name}
              </span>
              {showCode && (
                <span
                  data-testid={`audit-project-code-${e.id}`}
                  className={cn(
                    "text-muted-foreground text-xs tabular-nums truncate",
                    struck && "line-through decoration-1",
                  )}
                >
                  {e.project.code}
                </span>
              )}
            </div>
          );
        },
      },
      {
        // §20/18.x — слитная колонка «Работа»: вид работ сверху, подзадача
        // снизу (приглушённо), по аналогии с двухстрочным «Сотрудник» (имя/таб).
        id: "work",
        accessorFn: (r) => r.work_type?.name ?? r.work_type_text ?? "",
        enableSorting: false,
        header: t("audit.timesheet.columns.work"),
        cell: ({ row }) => {
          const e = row.original;
          const isUnknown = e.work_type == null && e.work_type_text != null;
          const workText = e.work_type?.name ?? e.work_type_text ?? "—";
          const struck = e.deleted_reason === "replaced" || e.deleted_reason === "user_delete";
          return (
            <div className="flex min-w-0 flex-col">
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 text-sm",
                  struck && "text-muted-foreground line-through decoration-1",
                  isUnknown && "text-[var(--tone-caution-fg)]",
                )}
              >
                {isUnknown && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <AlertTriangle
                        className="h-3.5 w-3.5 shrink-0"
                        aria-label={t("audit.timesheet.unknownWorkType")}
                      />
                    </TooltipTrigger>
                    <TooltipContent>{t("audit.timesheet.unknownWorkType")}</TooltipContent>
                  </Tooltip>
                )}
                <span className="truncate">{workText}</span>
              </span>
              {/* §115/2.3 — доработка без идентификатора (серверный subset-флаг
                  `is_unlabeled_rework` = derived `is_rework` без `rework_note`).
                  Причина-строка у записи по образцу skippedProject/declaredOffNorm;
                  амбер-полосу строки несёт общий `needs_attention`. */}
              {e.is_unlabeled_rework && (
                <span className="inline-flex items-center gap-1.5 text-xs text-[var(--tone-caution-fg)]">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <AlertTriangle
                        data-testid={`audit-rework-unlabeled-${e.id}`}
                        className="h-3.5 w-3.5 shrink-0"
                        aria-label={t("audit.timesheet.reworkUnlabeledHint")}
                      />
                    </TooltipTrigger>
                    <TooltipContent>{t("audit.timesheet.reworkUnlabeledHint")}</TooltipContent>
                  </Tooltip>
                  <span className="truncate">{t("audit.timesheet.reworkUnlabeled")}</span>
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: "hours",
        accessorKey: "hours_half",
        header: () => (
          <span className="block text-right">{t("audit.timesheet.columns.hours")}</span>
        ),
        cell: ({ row }) => {
          const e = row.original;
          const struck = e.deleted_reason === "replaced" || e.deleted_reason === "user_delete";
          return (
            <span
              className={cn(
                "block text-right font-medium tabular-nums",
                struck && "text-muted-foreground line-through decoration-1",
              )}
            >
              {formatHoursHalf(e.hours_half, t("audit.timesheet.hoursSuffix"))}
            </span>
          );
        },
      },
      {
        id: "declared",
        accessorKey: "declared_total_hours_half",
        enableSorting: false,
        header: () => (
          <span className="block text-right">{t("audit.timesheet.columns.declared")}</span>
        ),
        cell: ({ row }) => {
          const e = row.original;
          // §20/17.1 — «Табель» показывается только после подписи смены: до этого
          // declared = дефолт 8 ч, который рабочий ещё не подтвердил → «—».
          const isSigned = e.timesheet_status === "signed" || e.timesheet_status === "approved";
          if (!isSigned) {
            return (
              <span
                data-testid={`audit-declared-${e.id}`}
                className="block text-right text-muted-foreground"
              >
                —
              </span>
            );
          }
          // §20/18.1 — declared ≠ норме календарного дня (под-/переработка) →
          // амбер ⚠ как у unknown-project/work_type. Серверный флаг
          // `declared_off_norm` атомарен и независимо от других триггеров
          // подсказывает причину именно для этой колонки.
          const offNorm = e.declared_off_norm;
          return (
            <span
              data-testid={`audit-declared-${e.id}`}
              className={cn(
                "inline-flex w-full items-center justify-end gap-1.5 tabular-nums",
                offNorm && "text-[var(--tone-caution-fg)]",
              )}
            >
              {offNorm && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <AlertTriangle
                      data-testid={`audit-declared-warn-${e.id}`}
                      className="h-3.5 w-3.5 shrink-0"
                      aria-label={t("audit.timesheet.declaredOffNorm")}
                    />
                  </TooltipTrigger>
                  <TooltipContent>{t("audit.timesheet.declaredOffNorm")}</TooltipContent>
                </Tooltip>
              )}
              <span>
                {formatHoursHalf(e.declared_total_hours_half, t("audit.timesheet.hoursSuffix"))}
              </span>
            </span>
          );
        },
      },
      {
        id: "status",
        accessorKey: "timesheet_status",
        enableSorting: false,
        header: t("audit.timesheet.columns.status"),
        cell: ({ row }) => {
          const e = row.original;
          return (
            <span data-testid={`audit-status-${e.id}`} className="text-sm text-muted-foreground">
              {t(`audit.timesheet.status.${e.timesheet_status}` as const)}
            </span>
          );
        },
      },
      {
        id: "timeToSign",
        accessorKey: "time_to_sign_seconds",
        header: () => (
          <span className="block text-right">{t("audit.timesheet.columns.timeToSign")}</span>
        ),
        cell: ({ row }) => {
          const e = row.original;
          // §20/3.1: метрика «время до подписи» — кумулятив по сессиям рабочего.
          // Для admin-backfill (created_by_admin_id !== null) семантика не
          // применима — рисуем «—» без подсказки. Для NULL без backfill —
          // отчёт пока не подписан либо sign был без entries → тоже «—».
          if (e.time_to_sign_seconds == null) {
            return <span className="block text-right text-muted-foreground">—</span>;
          }
          return (
            <span className="block text-right text-sm tabular-nums text-muted-foreground">
              {formatHmsCompact(e.time_to_sign_seconds)}
            </span>
          );
        },
      },
      {
        id: "source",
        enableSorting: false,
        header: t("audit.timesheet.columns.source"),
        cell: ({ row }) => {
          const e = row.original;
          const isAdminBackfill = e.created_by_admin_id != null;
          const isReplaced = e.deleted_reason === "replaced";
          const isDeleted = e.deleted_reason === "user_delete";
          const isAdminDeleted = isDeleted && e.deleted_by != null && e.deleted_by !== e.user.id;
          const isUserDeleted = isDeleted && !isAdminDeleted;

          if (!isAdminBackfill && !isReplaced && !isDeleted) {
            return <span className="sr-only">—</span>;
          }
          const replacedTitle =
            e.followupId != null
              ? t("audit.timesheet.replacedTooltip", { id: e.followupId })
              : t("audit.timesheet.replacedTooltipNoRef");

          const at = e.deleted_at ? formatDateTime(e.deleted_at, locale) : null;
          const userDeletedLabel = at
            ? t("audit.timesheet.userDeletedAt", { at })
            : t("audit.timesheet.userDeletedTooltip");
          const adminDeletedLabel = at
            ? t("audit.timesheet.adminDeletedAt", { at })
            : t("audit.timesheet.adminDeletedTooltip");
          return (
            <div className="text-muted-foreground flex items-center gap-1.5">
              {isAdminBackfill && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      data-testid={`ts-audit-badge-admin-${e.id}`}
                      aria-label={t("audit.timesheet.adminBackfill")}
                      className="inline-flex"
                    >
                      <UserCog className="h-4 w-4" aria-hidden />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{t("audit.timesheet.adminBackfill")}</TooltipContent>
                </Tooltip>
              )}
              {isReplaced && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      data-testid={`ts-audit-badge-replaced-${e.id}`}
                      aria-label={replacedTitle}
                      className="inline-flex"
                    >
                      <History className="h-4 w-4" aria-hidden />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{replacedTitle}</TooltipContent>
                </Tooltip>
              )}
              {isUserDeleted && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      data-testid={`ts-audit-badge-deleted-${e.id}`}
                      aria-label={userDeletedLabel}
                      className="text-destructive inline-flex"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{userDeletedLabel}</TooltipContent>
                </Tooltip>
              )}
              {isAdminDeleted && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      data-testid={`ts-audit-badge-admin-deleted-${e.id}`}
                      aria-label={adminDeletedLabel}
                      className="text-destructive inline-flex"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{adminDeletedLabel}</TooltipContent>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        id: "actions",
        enableSorting: false,
        header: () => <span className="sr-only">{t("audit.timesheet.openInBackfill")}</span>,
        cell: ({ row }) => {
          const e = row.original;
          return (
            <div className="flex justify-end">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to="/time-approval"
                    search={{
                      user_id: e.user.id,
                      date: e.work_date,
                      week_start: isoMonday(e.work_date),
                    }}
                    data-testid={`ts-audit-open-backfill-${e.id}`}
                    aria-label={t("audit.timesheet.openInBackfill")}
                    className="text-muted-foreground hover:text-foreground inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-muted"
                  >
                    <ExternalLink className="h-4 w-4" aria-hidden />
                  </Link>
                </TooltipTrigger>
                <TooltipContent>{t("audit.timesheet.openInBackfill")}</TooltipContent>
              </Tooltip>
            </div>
          );
        },
      },
    ],
    [t, locale],
  );

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (!canView) {
    return (
      <p className="text-muted-foreground p-6 text-sm" data-testid="timesheet-audit-forbidden">
        {t("audit.timesheet.forbidden")}
      </p>
    );
  }

  const reset = (): void => setUi(EMPTY);

  const workerLabel =
    ui.workerId === "all"
      ? t("audit.timesheet.filters.all")
      : (workerById.get(ui.workerId)?.name ?? `#${ui.workerId}`);
  const projectLabel =
    ui.projectId === "all"
      ? t("audit.timesheet.filters.all")
      : (projectById.get(ui.projectId) ?? `#${ui.projectId}`);
  const workTypeLabel =
    ui.workTypeId === "all"
      ? t("audit.timesheet.filters.all")
      : (workTypeById.get(ui.workTypeId) ?? `#${ui.workTypeId}`);

  const colCount = columns.length;

  return (
    <TooltipProvider>
      <section data-testid="timesheet-audit-tab" className="flex flex-col gap-4">
        <header>
          <h2 className="text-lg font-semibold tracking-tight">{t("audit.timesheet.title")}</h2>
          <p className="text-muted-foreground mt-0.5 text-sm">{t("audit.timesheet.subtitle")}</p>
        </header>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2" data-testid="timesheet-audit-filters">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="ts-audit-worker-filter">
                {t("audit.timesheet.filters.worker")}: {workerLabel}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
              <DropdownMenuItem
                data-testid="ts-audit-worker-option-all"
                onSelect={() => setUi((s) => ({ ...s, workerId: "all" }))}
              >
                {t("audit.timesheet.filters.all")}
              </DropdownMenuItem>
              {(workers.data ?? []).map((w) => (
                <DropdownMenuItem
                  key={w.id}
                  data-testid={`ts-audit-worker-option-${w.id}`}
                  onSelect={() => setUi((s) => ({ ...s, workerId: w.id }))}
                >
                  {w.display_name}
                  {w.tab_number && (
                    <span className="text-muted-foreground ml-2 text-xs tabular-nums">
                      {t("audit.timesheet.tab", { value: w.tab_number })}
                    </span>
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="ts-audit-project-filter">
                {t("audit.timesheet.filters.project")}: {projectLabel}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
              <DropdownMenuItem
                data-testid="ts-audit-project-option-all"
                onSelect={() => setUi((s) => ({ ...s, projectId: "all" }))}
              >
                {t("audit.timesheet.filters.all")}
              </DropdownMenuItem>
              {(projectsQ.data ?? []).map(
                (p) =>
                  typeof p.id === "number" && (
                    <DropdownMenuItem
                      key={p.id}
                      data-testid={`ts-audit-project-option-${p.id}`}
                      onSelect={() => setUi((s) => ({ ...s, projectId: p.id as number }))}
                    >
                      {p.name}
                    </DropdownMenuItem>
                  ),
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="ts-audit-worktype-filter">
                  {t("audit.timesheet.filters.workType")}: {workTypeLabel}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
                <DropdownMenuItem
                  data-testid="ts-audit-worktype-option-all"
                  onSelect={() => setUi((s) => ({ ...s, workTypeId: "all" }))}
                >
                  {t("audit.timesheet.filters.all")}
                </DropdownMenuItem>
                {(workTypesQ.data ?? []).map(
                  (w) =>
                    typeof w.id === "number" && (
                      <DropdownMenuItem
                        key={w.id}
                        data-testid={`ts-audit-worktype-option-${w.id}`}
                        onSelect={() => setUi((s) => ({ ...s, workTypeId: w.id as number }))}
                      >
                        {w.name}
                      </DropdownMenuItem>
                    ),
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <label className="text-muted-foreground flex items-center gap-1 text-xs">
            {t("audit.timesheet.filters.dateFrom")}
            <DateField
              data-testid="ts-audit-date-from"
              value={ui.dateFrom}
              onChange={(value) => setUi((s) => ({ ...s, dateFrom: value ?? "" }))}
              label={t("audit.timesheet.filters.dateFrom")}
              className="h-9 w-[140px]"
            />
          </label>
          <label className="text-muted-foreground flex items-center gap-1 text-xs">
            {t("audit.timesheet.filters.dateTo")}
            <DateField
              data-testid="ts-audit-date-to"
              value={ui.dateTo}
              onChange={(value) => setUi((s) => ({ ...s, dateTo: value ?? "" }))}
              label={t("audit.timesheet.filters.dateTo")}
              className="h-9 w-[140px]"
            />
          </label>

          {/* Admin-only фильтры (task 82 §1.4) — лиду не показываем. */}
          {isAdmin && (
            <>
              <label className="text-foreground/80 ml-1 flex cursor-pointer items-center gap-1.5 text-xs">
                <input
                  type="checkbox"
                  data-testid="ts-audit-show-deleted"
                  checked={ui.showDeleted}
                  onChange={(e) => setUi((s) => ({ ...s, showDeleted: e.target.checked }))}
                  className="border-input h-4 w-4 rounded border accent-current"
                />
                {t("audit.timesheet.filters.showDeleted")}
              </label>
              <label className="text-foreground/80 flex cursor-pointer items-center gap-1.5 text-xs">
                <input
                  type="checkbox"
                  data-testid="ts-audit-needs-attention"
                  checked={ui.needsAttention}
                  onChange={(e) => setUi((s) => ({ ...s, needsAttention: e.target.checked }))}
                  className="border-input h-4 w-4 rounded border accent-current"
                />
                {t("audit.timesheet.filters.needsAttention")}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      data-testid="ts-audit-needs-attention-help"
                      aria-label={t("audit.timesheet.filters.needsAttentionHelp.title")}
                      className="text-muted-foreground hover:text-foreground shrink-0 cursor-pointer"
                    >
                      <HelpCircle className="h-3.5 w-3.5" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    className="w-80 space-y-2"
                    data-testid="ts-audit-needs-attention-help-content"
                  >
                    <p className="text-foreground text-sm font-medium">
                      {t("audit.timesheet.filters.needsAttentionHelp.title")}
                    </p>
                    <ul className="text-muted-foreground list-disc space-y-0.5 pl-4 text-sm">
                      {(
                        t("audit.timesheet.filters.needsAttentionHelp.items", {
                          returnObjects: true,
                        }) as string[]
                      ).map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </PopoverContent>
                </Popover>
              </label>
              <label className="text-foreground/80 flex cursor-pointer items-center gap-1.5 text-xs">
                <input
                  type="checkbox"
                  data-testid="ts-audit-admin-backfill-only"
                  checked={ui.adminBackfillOnly}
                  onChange={(e) => setUi((s) => ({ ...s, adminBackfillOnly: e.target.checked }))}
                  className="border-input h-4 w-4 rounded border accent-current"
                />
                {t("audit.timesheet.filters.adminBackfillOnly")}
              </label>
            </>
          )}

          <Button variant="ghost" size="sm" data-testid="ts-audit-reset" onClick={reset}>
            {t("audit.timesheet.filters.reset")}
          </Button>
        </div>

        {/* Table */}
        <div className="rounded-[var(--radius-card)] border bg-[var(--bg-panel-0)]">
          <Table>
            <TableHeader className="sticky top-0 z-10 border-b border-[var(--rule-line)] bg-[var(--bg-panel-0)]">
              {table.getHeaderGroups().map((hg) => (
                <TableRow key={hg.id}>
                  {hg.headers.map((h) => (
                    <SortableTableHead
                      key={h.id}
                      className="bg-[var(--bg-panel-0)]"
                      sortable={h.column.getCanSort()}
                      direction={h.column.getIsSorted()}
                      onToggle={h.column.getToggleSortingHandler()}
                      data-testid={
                        h.column.getCanSort() ? `ts-audit-sort-${h.column.id}` : undefined
                      }
                    >
                      {flexRender(h.column.columnDef.header, h.getContext())}
                    </SortableTableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {feed.isLoading ? (
                <TableRow>
                  <TableCell colSpan={colCount} className="text-muted-foreground py-6 text-center">
                    {t("audit.timesheet.loading")}
                  </TableCell>
                </TableRow>
              ) : feed.isError ? (
                <TableRow>
                  <TableCell colSpan={colCount} className="py-6 text-center">
                    <ApiErrorNotice
                      error={feed.error}
                      fallbackMessage={t("common.apiError.fallback")}
                      className="text-destructive"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-2"
                      onClick={() => void feed.refetch()}
                      data-testid="ts-audit-retry"
                    >
                      <RefreshCw className="mr-1 h-3 w-3" />
                      {t("common.retry")}
                    </Button>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={colCount}
                    className="text-muted-foreground py-6 text-center"
                    data-testid="ts-audit-empty"
                  >
                    {t("audit.timesheet.empty")}
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row) => {
                  const e = row.original;
                  // §20/18.1: серверный флаг — покрывает триггер «declared ≠
                  // норме календарного дня» (signed/approved отчёт под-/
                  // переработан), который из чисто клиентских полей невычислим
                  // без знания нормы дня. Per-cell-стилистика для unknown/
                  // skipped project и unknown work_type — внутри cell-renderer'ов.
                  const needsAttention = e.needs_attention;
                  const isUserDeleted = e.deleted_reason === "user_delete";
                  const isReplaced = e.deleted_reason === "replaced";
                  return (
                    <TableRow
                      key={row.id}
                      data-testid={`ts-audit-row-${e.id}`}
                      data-needs-attention={needsAttention || undefined}
                      data-deleted={isUserDeleted || undefined}
                      data-replaced={isReplaced || undefined}
                      // border-collapse:collapse у таблицы съедает border-left
                      // на всех строках кроме первой — амбер-полосу рисуем
                      // через inset box-shadow на первой ячейке, чтобы стрипа
                      // была у каждой needs_attention-строки.
                      className={cn(
                        needsAttention &&
                          "[&>td:first-child]:shadow-[inset_4px_0_0_var(--color-amber-500)]",
                      )}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {feed.hasNextPage && (
          <div className="flex justify-center">
            <Button
              variant="outline"
              size="sm"
              data-testid="ts-audit-load-more"
              disabled={feed.isFetchingNextPage}
              onClick={() => void feed.fetchNextPage()}
            >
              {feed.isFetchingNextPage
                ? t("audit.timesheet.loading")
                : t("audit.timesheet.loadMore")}
            </Button>
          </div>
        )}
      </section>
    </TooltipProvider>
  );
}
